import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { getEventTools } from "../lib/api";
import type { EventTool } from "../lib/types";

/**
 * Subscribes to the event_tools table for a given event. Returns the
 * insight-tool catalog sorted by sort_order. Re-fetches on realtime
 * INSERT/UPDATE/DELETE so the facilitator's edits propagate to the AI panel
 * and the stage view live.
 *
 * Multiple components may mount this hook for the same event (e.g. the
 * facilitator view and the tools editor side-by-side), so each instance
 * gets its own channel suffix — Supabase Realtime refuses to register
 * listeners on a channel that's already subscribed.
 */
export function useEventTools(eventId: string | null) {
  const [tools, setTools] = useState<EventTool[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const instanceIdRef = useRef<string>(Math.random().toString(36).slice(2, 10));

  const load = useCallback(async () => {
    if (!eventId) {
      setTools([]);
      setLoading(false);
      return;
    }
    const data = await getEventTools(eventId);
    setTools(data);
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    load();
    if (!eventId) return;

    const channel = supabase
      .channel(`event_tools-${eventId}-${instanceIdRef.current}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_tools", filter: `event_id=eq.${eventId}` },
        () => load(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, load]);

  return { tools, loading, reload: load };
}
